import React, { useState, useEffect } from "react";
import { Box, Flex, Heading, Icon, Text, useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import useThemeColors from "../../../../../hooks/useThemeColors";
import FormControlDefault from "../../../../../components/input/FormControlDefault";
import { textSecondary } from "../../../../../utils/colors";
import SaveButtons from "./SaveButtons";
import { useDispatch, useSelector } from "react-redux";
import { TENANT_UPDATE_CLINIC_DETAILS_RESET } from "../../../../../constants/tenantConstants";
import { FaGoogle } from "react-icons/fa6";

const UserProfileDetails = ({ slug, tenant, userInfo }) => {
  // GENERAL
  // const { slug } = useParams();
  const { t } = useTranslation();
  const toast = useToast();

  // COLORS
  const { backgroundLightMode, newBorderMode } = useThemeColors();

  // STATE
  const [showButtons, setShowButtons] = useState(false);

  // const [clinicName, setClinicName] = useState(tenant?.clinicName || "");
  const [email, setEmail] = useState(userInfo?.email || "");
  const [supportEmail, setSupportEmail] = useState(tenant?.supportEmail || "");
  const [phone, setPhone] = useState(tenant?.phone || "");
  const [website, setWebsite] = useState(tenant?.website || "");

  const [name, setName] = useState(userInfo?.name || "");

  const [address, setAddress] = useState(() => ({
    street: tenant?.address?.street || "",
    city: tenant?.address?.city || "",
    country: tenant?.address?.country || "",
    zip: tenant?.address?.zip || "",
    state: tenant?.address?.state || "",
    display: tenant?.address
      ? `${tenant.address.street}, ${tenant.address.city}, ${tenant.address.zip}`
      : "",
  }));

  const [manualAddressVisible, setManualAddressVisible] = useState(false);

  // REDUX
  const dispatch = useDispatch();

  const tenantUpdateClinicDetails = useSelector(
    (state) => state.tenantUpdateClinicDetails
  );
  const {
    loading: loadingUpdateClinicDetails,
    error: errorUpdateClinicDetails,
    success: successUpdateClinicDetails,
  } = tenantUpdateClinicDetails;

  // HANDLERS
  const updateClinicDetailsHandler = () => {
    console.log("User details to be sent:", {
      name,
      supportEmail,
      phone,
      address,
    }); // ✅ Debugging log
  };
  // USE EFFECT

  const normalizeValue = (value) =>
    value === undefined || value === null ? "" : String(value).trim();

  const normalizePhone = (phone) =>
    phone === undefined || phone === null || phone === "" || phone === "+370"
      ? ""
      : phone;

  useEffect(() => {
    if (
      normalizeValue(name) !== normalizeValue(userInfo?.name) ||
      normalizeValue(email) !== normalizeValue(userInfo?.email) ||
      normalizeValue(supportEmail) !== normalizeValue(tenant?.supportEmail)
      // hasAddressChanged ||
      // hasPhoneChanged
    ) {
      // console.log("Setting showButtons to TRUE");
      setShowButtons(true);
    } else {
      // console.log("Setting showButtons to FALSE");
      setShowButtons(false);
    }
  }, [email, phone, supportEmail, address, name]);

  useEffect(() => {
    if (successUpdateClinicDetails) {
      toast({
        title: t("toasts.clinicDetailsUpdated"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setShowButtons(false);
    } else if (errorUpdateClinicDetails) {
      toast({
        title: t("toasts.clinicDetailsUpdatedError"),
        status: "error",
        duration: 3000,

        isClosable: true,
      });
    }

    dispatch({ type: TENANT_UPDATE_CLINIC_DETAILS_RESET });
  }, [successUpdateClinicDetails, errorUpdateClinicDetails]);

  const isGoogleAccount = userInfo?.googleAccount || false;

  // LOGS
  // console.log("tenant", tenant);

  return (
    <Box
      w="100%"
      p={["1.5rem 0rem 0rem", "3rem 0rem 3rem"]}
      borderBottom={[
        `0px solid ${newBorderMode}`,
        `1px solid ${newBorderMode}`,
      ]}
    >
      <Flex
        w="100%"
        justify="space-between"
        gap={["1.5rem", "2rem"]}
        direction={["column", "row"]}
      >
        {/* LEFT START */}
        <Flex
          direction="column"
          gap={2}
          w={["100%", "40%"]}
          p={["0 1.5rem", "initial"]}
        >
          <Heading fontSize="1rem" fontWeight="600">
            {t("user.userDetails")}
          </Heading>
          <Text color={textSecondary} fontSize="sm" fontWeight="500">
            {t("user.userDetailsDescription")}
          </Text>
        </Flex>
        {/* LEFT END */}

        {/* RIGHT START */}
        <Flex
          w={["100%", "800px"]}
          direction="column"
          justify="flex-start"
          align="flex-start"
        >
          <Flex
            direction="column"
            w="100%"
            bg={backgroundLightMode}
            borderRadius={["0", "1rem"]}
            p={["1.25rem 1.5rem 1.25rem", "1.25rem 1.5rem 1.25rem"]}
          >
            <FormControlDefault
              // autoFocus
              type="text"
              size="lg"
              labelFontSize="0.8rem"
              isRequired
              label={t("form.name")}
              value={name}
              placeholder={t("placeholder.name")}
              onChange={(e) => {
                setName(e.target.value);
              }}
              w={["100%", "100%", "100%"]}
            />

            <Flex w="100%">
              <FormControlDefault
                type="email"
                size="lg"
                labelFontSize="0.8rem"
                isRequired
                label={t("form.email")}
                value={email}
                placeholder={t("placeholder.email")}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
                // isError={isEmailValidated}
                w="100%"
                errorMessage={t("formError.emailRequired")}
                isDisabled
              />
            </Flex>
            {isGoogleAccount && (
              <Flex align="center">
                <Icon as={FaGoogle} fontSize="0.75rem" color="gray.500" />
                <Text
                  fontSize="0.8rem"
                  fontWeight="500"
                  color={textSecondary}
                  ml={2}
                >
                  {t("user.googleAccount")}
                </Text>
              </Flex>
            )}
          </Flex>
        </Flex>
        {/* RIGHT END */}
      </Flex>

      {/* BUTTONS */}
      {showButtons && (
        <SaveButtons
          onClose={() => {
            setShowButtons(false);
            setName(userInfo?.name || "");
            setEmail(userInfo?.email || "");
            setSupportEmail(tenant?.supportEmail || "");
            setPhone(tenant?.phone || "");
            setWebsite(tenant?.website || "");
            setAddress({
              street: tenant?.address?.street || "",
              city: tenant?.address?.city || "",
              country: tenant?.address?.country || "",
              zip: tenant?.address?.zip || "",
              state: tenant?.address?.state || "",
              display: tenant?.address
                ? `${tenant.address.street}, ${tenant.address.city}, ${tenant.address.zip}`
                : "",
            });
          }}
          onClick={updateClinicDetailsHandler}
          loading
        />
      )}
    </Box>
  );
};

export default UserProfileDetails;
